import React, { useState } from 'react';
import { supabase } from '../config/supabase';

const DeveloperToolsPage: React.FC = () => {
  const [results, setResults] = useState<string>('');
  const [loading, setLoading] = useState(false);
  
  // 🆕 Manual like için state'ler
  const [fromEmail, setFromEmail] = useState('');
  const [showManualLikeForm, setShowManualLikeForm] = useState(false);
  
  // 🚀 Herkesten beğeni gönder için state'ler
  const [massLikeEmail, setMassLikeEmail] = useState('');
  const [showMassLikeForm, setShowMassLikeForm] = useState(false);
  
  // 💕 Tüm erkekleri beğen için state'ler
  const [likeAllMalesEmail, setLikeAllMalesEmail] = useState('');
  const [showLikeAllMalesForm, setShowLikeAllMalesForm] = useState(false);

  // 📝 Doğal ve çeşitli post içerikleri (gerçek kullanıcı gibi)
  const postContents = [
    // Kısa ve sade
    "günaydın ☀️",
    "bugün güzel bir gün",
    "kahve zamanı ☕",
    "herkese iyi akşamlar",
    "mutlu hissediyorum 😊",
    "yeni bir gün, yeni umutlar",
    "hayat güzel",
    "bugün enerjim yüksek",
    
    // Günlük paylaşımlar
    "arkadaşlarla buluştuk, çok eğlendik",
    "uzun zamandır görmediğim arkadaşımla kahve içtik",
    "bugün kendime vakit ayırdım",
    "hafta sonu modu on 🎉",
    "pazartesi sendromu yaşayanlar +1",
    "cuma akşamı planları?",
    "evde film gecesi 🎬",
    "yürüyüşe çıktım, hava harika",
    
    // Yemek & mekan
    "bu kahve harika ☕",
    "yeni keşfettiğim mekan çok güzel",
    "brunch keyfi 🥐",
    "akşam yemeği hazırlıyorum",
    "tatlı krizine girdim 🍰",
    "sıcak çorba günü",
    
    // Hava & mevsim
    "güneşli günlerin tadını çıkarıyorum",
    "yağmurlu havayı seviyorum 🌧️",
    "sonbahar havası çok güzel",
    "bu hava tam yürüyüş havası",
    
    // Motivasyon
    "kendine inan",
    "her şey güzel olacak",
    "bugün iyi şeyler olacak",
    "pozitif düşün, pozitif yaşa",
    "hayallerine inan ✨",
    
    // Soru & etkileşim
    "sizin gününüz nasıl geçiyor?",
    "hafta sonu planınız ne?",
    "en sevdiğiniz mevsim hangisi?",
    "kahve mi çay mı?",
    
    // Aktivite
    "spor yaptım, kendimi çok iyi hissediyorum 💪",
    "yoga günü 🧘‍♀️",
    "kitap okuma zamanı 📚",
    "müzik dinliyorum 🎵",
    "yeni bir hobi deniyorum",
    
    // Duygusal
    "bugün kendimi şanslı hissediyorum",
    "minnettar olduğum şeyler var",
    "küçük şeylerin mutluluğu",
    "anı yaşamak güzel",
    "huzurlu bir gün",
    
    // Basit günlük
    "güzel bir gün geçirdim",
    "yorucu ama güzel bir gün",
    "dinlenme zamanı",
    "erken kalktım bugün",
    "geç kalktım ama olsun 😅"
  ];

  // Supabase varsayılan 1000 satır limitine takılmamak için sayfalı okuma yardımcıları
  const PAGE_SIZE = 1000;

  const fetchAllPages = async <T,>(
    buildQuery: (from: number, to: number) => any,
    label: string
  ): Promise<T[]> => {
    const rows: T[] = [];
    let from = 0;

    while (true) {
      const to = from + PAGE_SIZE - 1;
      const { data, error } = await buildQuery(from, to);

      if (error) {
        throw new Error(`${label} alınamadı: ${error.message}`);
      }

      if (!data || data.length === 0) {
        break;
      }

      rows.push(...data);

      if (data.length < PAGE_SIZE) {
        break;
      }
      from += PAGE_SIZE;
    }

    return rows;
  };

  const getOrderedUserPair = (userA: string, userB: string) => {
    return userA < userB
      ? { user1: userA, user2: userB }
      : { user1: userB, user2: userA };
  };

  // İstanbul civarına konum atama işlemi
  const handleAssignIstanbulLocations = async () => {
    if (!window.confirm('Tüm kullanıcılara İstanbul civarında rastgele konum atamak istediğinizden emin misiniz?')) {
      return;
    }

    setLoading(true);
    const timestamp = new Date().toLocaleTimeString();
    
    try {
      setResults(prev => prev + `🔄 [${timestamp}] İstanbul civarına konum atama başlatılıyor...\n`);

      // Tüm kullanıcıları al
      const { data: users, error } = await supabase
        .from('users')
        .select('id, name, location')
        .limit(1000);

      if (error) {
        throw new Error(`Kullanıcılar alınamadı: ${error.message}`);
      }

      if (!users || users.length === 0) {
        setResults(prev => prev + '❌ Güncellenecek kullanıcı bulunamadı\n\n');
        return;
      }

      setResults(prev => prev + `📊 ${users.length} kullanıcı bulundu, konum güncelleme başlıyor...\n`);

      let processedCount = 0;
      let errorCount = 0;
      const appendResult = (message: string) => setResults(prev => prev + message);

      // İstanbul koordinat sınırları
      const istanbulBounds = {
        north: 41.2,
        south: 40.8,
        east: 29.2,
        west: 28.6
      };

      const updateUserLocation = async (user: any) => {
        try {
          // İstanbul civarında rastgele koordinat üret
          const latitude = istanbulBounds.south + Math.random() * (istanbulBounds.north - istanbulBounds.south);
          const longitude = istanbulBounds.west + Math.random() * (istanbulBounds.east - istanbulBounds.west);

          const locationData = {
            city: 'İstanbul',
            country: 'Türkiye',
            latitude: parseFloat(latitude.toFixed(6)),
            longitude: parseFloat(longitude.toFixed(6))
          };

          const { error: updateError } = await supabase
            .from('users')
            .update({
              location: locationData,
              updated_at: new Date().toISOString()
            })
            .eq('id', user.id);

          if (updateError) {
            throw updateError;
          }

          processedCount++;
        } catch (error) {
          console.error(`Kullanıcı ${user.id} konumu güncellenemedi:`, error);
          errorCount++;
        }
      };

      // Kullanıcıları 10'luk gruplar halinde işle
      const batchSize = 10;
      for (let i = 0; i < users.length; i += batchSize) {
        const batch = users.slice(i, i + batchSize);
        
        const updatePromises = batch.map(updateUserLocation);

        await Promise.all(updatePromises);

        // İlerleme göster
        const progress = Math.round((processedCount / users.length) * 100);
        appendResult(`📍 ${processedCount}/${users.length} kullanıcı işlendi (%${progress})\n`);

        // Rate limiting için kısa bekleme
        if (i + batchSize < users.length) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      setResults(prev => prev + `✅ Konum atama tamamlandı!\n`);
      setResults(prev => prev + `📊 Başarılı: ${processedCount}, Hatalı: ${errorCount}\n`);
      setResults(prev => prev + `📍 Tüm kullanıcılar artık İstanbul civarında görünüyor.\n\n`);

    } catch (error: any) {
      setResults(prev => prev + `❌ [${timestamp}] Hata: ${error.message}\n\n`);
    } finally {
      setLoading(false);
    }
  };

  // 📝 50 Rastgele Post Oluşturma işlemi (Sadece Fake Kadın Kullanıcılar)
  const handleCreate50RandomPosts = async () => {
    if (!window.confirm('50 adet post oluşturmak istediğinizden emin misiniz?\n\n• 25 post fotoğraflı\n• 25 post sadece metin\n• Sadece fake kadın kullanıcılar post atacak')) {
      return;
    }

    setLoading(true);
    const timestamp = new Date().toLocaleTimeString();
    
    try {
      setResults(prev => prev + `🔄 [${timestamp}] Fake kadın kullanıcılar sorgulanıyor...\n`);

      // Sadece fake kadın kullanıcıları çek (test.kadın.X@datezy.com formatında olanlar)
      const { data: fakeUsers, error: usersError } = await supabase
        .from('users')
        .select('id, name, profile_pic_url, email')
        .like('email', 'test.kadın.%@datezy.com')
        .not('profile_pic_url', 'is', null)
        .limit(35);

      console.log('🔍 Fake kullanıcı sorgusu sonucu:', { fakeUsers, usersError });
      setResults(prev => prev + `📊 Fake kullanıcı sorgusu: ${fakeUsers?.length || 0} kullanıcı, hata: ${usersError ? usersError.message : 'YOK'}\n`);

      if (usersError) {
        throw new Error(`Fake kullanıcılar alınamadı: ${usersError.message}`);
      }

      if (!fakeUsers || fakeUsers.length === 0) {
        throw new Error('Hiç fake kadın kullanıcı bulunamadı. Önce "Fake Kullanıcı Yönet" sayfasından kullanıcı oluşturun.');
      }

      // Sadece gerçek Türk fotoğrafları olan kullanıcıları filtrele (datezygo.com/Datezyfake/ URL'leri)
      const realPhotoUsers = fakeUsers.filter(user => 
        user.profile_pic_url && user.profile_pic_url.includes('datezygo.com/Datezyfake/')
      );

      const usersToUse = realPhotoUsers.length > 0 ? realPhotoUsers : fakeUsers;
      
      setResults(prev => prev + `👩 ${fakeUsers.length} fake kadın kullanıcı bulundu\n`);
      setResults(prev => prev + `🇹🇷 ${realPhotoUsers.length} kullanıcı gerçek Türk fotoğrafına sahip\n`);
      setResults(prev => prev + `🔄 50 post oluşturuluyor (25 fotoğraflı + 25 metin)...\n`);

      let successCount = 0;
      let errorCount = 0;
      let photoPostCount = 0;
      let textPostCount = 0;

      // 50 post oluştur (25 fotoğraflı + 25 sadece metin)
      for (let i = 0; i < 50; i++) {
        try {
          // Rastgele fake kadın kullanıcı seç
          const randomUser = usersToUse[Math.floor(Math.random() * usersToUse.length)];
          
          // Rastgele içerik seç (her seferinde farklı olması için shuffle)
          const randomContent = postContents[Math.floor(Math.random() * postContents.length)];
          
          // Post ID oluştur
          const postId = `profile_post_${Date.now()}_${i}`;
          
          // İlk 25 post fotoğraflı, son 25 post sadece metin
          const hasImage = i < 25;
          
          // Post data
          const postData = {
            id: postId,
            user_id: randomUser.id,
            content: randomContent,
            user_name: randomUser.name || 'Test User',
            user_profile_pic_url: randomUser.profile_pic_url || null,
            image_urls: hasImage && randomUser.profile_pic_url ? [randomUser.profile_pic_url] : null,
            like_count: Math.floor(Math.random() * 50),
            comment_count: Math.floor(Math.random() * 20),
            liked_by_user_ids: [],
            is_deleted: false,
            created_at: new Date().toISOString(),
            expires_at: new Date(Date.now() + 90*24*60*60*1000).toISOString(), // 3 ay sonra
            updated_at: new Date().toISOString()
          };

          // Supabase'e gönder
          const { error } = await supabase
            .from('posts')
            .insert([postData])
            .select();

          if (error) {
            console.error(`❌ Post ${i + 1} hatası:`, error);
            errorCount++;
          } else {
            successCount++;
            if (hasImage) {
              photoPostCount++;
            } else {
              textPostCount++;
            }
          }

          // Her post arası kısa bekleme
          await new Promise(resolve => setTimeout(resolve, 100));
          
        } catch (error: any) {
          console.error(`💥 Post ${i + 1} kritik hata:`, error);
          errorCount++;
        }
      }

      setResults(prev => prev + `\n🏁 POST OLUŞTURMA TAMAMLANDI!\n`);
      setResults(prev => prev + `📊 SONUÇ: Başarılı: ${successCount}, Hatalı: ${errorCount}\n`);
      setResults(prev => prev + `🖼️ Fotoğraflı post: ${photoPostCount}\n`);
      setResults(prev => prev + `📝 Sadece metin post: ${textPostCount}\n`);
      setResults(prev => prev + `👩 ${usersToUse.length} fake kadın kullanıcı arasından seçim yapıldı\n`);
      if (successCount > 0) {
        setResults(prev => prev + `✅ ${successCount} post başarıyla oluşturuldu!\n`);
        setResults(prev => prev + `📱 Uygulamanızın akış sayfasını kontrol edin\n`);
        setResults(prev => prev + `🇹🇷 Tüm postlar fake Türk kadın kullanıcılar tarafından atıldı\n`);
      }
      if (errorCount > 0) {
        setResults(prev => prev + `❌ ${errorCount} post oluşturulamadı\n`);
      }

    } catch (error: any) {
      setResults(prev => prev + `❌ [${timestamp}] Genel hata: ${error.message}\n\n`);
    } finally {
      setLoading(false);
    }
  };

  // 🆕 Manual Like: yazılan kullanıcı tüm erkekleri beğensin (sayfalı çekim ile)
  const handleManualLike = async () => {
    if (!fromEmail.trim()) {
      setResults(prev => prev + '❌ Lütfen beğenecek kullanıcının email adresini girin!\n');
      return;
    }

    setLoading(true);
    const timestamp = new Date().toLocaleTimeString();
    
    try {
      setResults(prev => prev + `💕 [${timestamp}] Tüm erkekleri beğenme işlemi başlatılıyor: ${fromEmail}\n`);

      // 1. Beğenecek kullanıcıyı bul
      const { data: likerUser, error: likerError } = await supabase
        .from('users')
        .select('id, name, email')
        .eq('email', fromEmail.trim())
        .single();

      if (likerError || !likerUser) {
        throw new Error(`Kullanıcı bulunamadı: ${fromEmail}`);
      }

      setResults(prev => prev + `👤 Beğenen kullanıcı: ${likerUser.name} (${likerUser.email})\n`);

      // 2. Bu kullanıcının daha önce swipe ettiği kişileri sayfalı çek
      const existingSwipes = await fetchAllPages<{ swiped_id: string }>(
        (from, to) =>
          supabase
            .from('user_swipes')
            .select('swiped_id')
            .eq('swiper_id', likerUser.id)
            .range(from, to),
        'Mevcut swipe listesi'
      );

      const alreadySwipedUserIds = new Set(existingSwipes.map(l => l.swiped_id));
      setResults(prev => prev + `📊 Zaten swipe etmiş: ${alreadySwipedUserIds.size} kullanıcı\n`);

      // 3. Tüm ERKEK aktif kullanıcıları sayfalı çek (kendisi hariç)
      const allMaleUsers = await fetchAllPages<{ id: string; name: string; email: string; gender: string }>(
        (from, to) =>
          supabase
            .from('users')
            .select('id, name, email, gender')
            .neq('id', likerUser.id)
            .eq('is_active', true)
            .in('gender', ['male', 'Erkek', 'erkek', 'Male'])
            .range(from, to),
        'Erkek kullanıcı listesi'
      );

      if (allMaleUsers.length === 0) {
        throw new Error('Beğenilecek erkek kullanıcı bulunamadı');
      }

      // 4. Henüz swipe etmediği erkekleri filtrele
      const usersToLike = allMaleUsers.filter(u => !alreadySwipedUserIds.has(u.id));
      
      setResults(prev => prev + `👨 Toplam erkek kullanıcı: ${allMaleUsers.length}\n`);
      setResults(prev => prev + `💕 Beğenilecek erkek: ${usersToLike.length}\n`);

      if (usersToLike.length === 0) {
        setResults(prev => prev + '✅ Bu kullanıcı zaten tüm erkekleri swipe etmiş!\n\n');
        setFromEmail('');
        return;
      }

      // 5. Her erkek için like upsert et (pass varsa like'a çevirir)
      let successCount = 0;
      let errorCount = 0;
      let matchCount = 0;

      // İlk insert'ten önce doğrulama: service_role key kontrolü
      {
        const testPayload = {
          id: `__test_${likerUser.id}_${Date.now()}`,
          swiper_id: likerUser.id,
          swiped_id: likerUser.id,
          action: 'test',
          created_at: new Date().toISOString(),
        };
        const { data: testData, error: testError, status: testStatus } = await supabase
          .from('user_swipes')
          .insert(testPayload)
          .select();
        console.log('🧪 TEST INSERT status:', testStatus, 'data:', testData, 'error:', testError);
        setResults(prev => prev + `🧪 Test insert status: ${testStatus}, data: ${JSON.stringify(testData)}, error: ${testError?.message || 'yok'}\n`);
        
        // Test kaydını sil
        if (testData && testData.length > 0) {
          await supabase.from('user_swipes').delete().eq('id', testPayload.id);
          setResults(prev => prev + `✅ Test insert başarılı! Service role key çalışıyor.\n`);
        } else {
          setResults(prev => prev + `⚠️ Test insert başarısız! Muhtemelen anon key kullanılıyor veya RLS engelliyor.\n`);
          setResults(prev => prev + `⚠️ Admin panelini yeniden başlatın (Ctrl+Shift+R veya terminal'den restart).\n\n`);
        }
      }

      for (let i = 0; i < usersToLike.length; i++) {
        const maleUser = usersToLike[i];
        
        try {
          // user_swipes tablosuna insert
          const { data: swipeData, error: upsertError, status } = await supabase
            .from('user_swipes')
            .upsert(
              {
                id: `${likerUser.id}_${maleUser.id}`,
                swiper_id: likerUser.id,
                swiped_id: maleUser.id,
                action: 'like',
                created_at: new Date().toISOString(),
              },
              { onConflict: 'id' }
            )
            .select();

          // İlk 3 sonucu detaylı logla
          if (i < 3) {
            console.log(`[${i}] user_swipes upsert → status: ${status}, data:`, swipeData, 'error:', upsertError);
            setResults(prev => prev + `🔍 [${i}] swipe status:${status} data:${JSON.stringify(swipeData)} err:${upsertError?.message || 'yok'}\n`);
          }

          if (upsertError) {
            console.error(`Erkek ${maleUser.id} beğenilemedi:`, upsertError);
            errorCount++;
          } else {
            // user_likes tablosuna da ekle (uygulama gibi)
            const { error: likesError } = await supabase
              .from('user_likes')
              .upsert(
                {
                  from_user_id: likerUser.id,
                  to_user_id: maleUser.id,
                  created_at: new Date().toISOString(),
                },
                { onConflict: 'from_user_id,to_user_id', ignoreDuplicates: true }
              );
            if (i < 3 && likesError) {
              console.error(`[${i}] user_likes hatası:`, likesError);
              setResults(prev => prev + `⚠️ [${i}] user_likes err: ${likesError.message}\n`);
            }
            successCount++;
          }
        } catch (err) {
          if (i < 3) console.error(`[${i}] catch hatası:`, err);
          errorCount++;
        }

        if ((i + 1) % 50 === 0 || i === usersToLike.length - 1) {
          const sc = successCount, ec = errorCount;
          const progress = Math.round(((i + 1) / usersToLike.length) * 100);
          setResults(prev => prev + `📍 İlerleme: ${i + 1}/${usersToLike.length} (%${progress}) - ✅${sc} ❌${ec}\n`);
        }

        if ((i + 1) % 10 === 0) {
          await new Promise(resolve => setTimeout(resolve, 50));
        }
      }

      // 6. Karşılıklı beğenileri sayfalı çek ve match oluştur
      setResults(prev => prev + `\n🔍 Karşılıklı beğeniler kontrol ediliyor...\n`);
      const whoLikesUser = await fetchAllPages<{ swiper_id: string }>(
        (from, to) =>
          supabase
            .from('user_swipes')
            .select('swiper_id')
            .eq('swiped_id', likerUser.id)
            .eq('action', 'like')
            .range(from, to),
        'Karşılıklı beğeni listesi'
      );

      const whoLikesIds = new Set(whoLikesUser.map(l => l.swiper_id));
      const potentialMatches = usersToLike.filter(u => whoLikesIds.has(u.id));
      
      if (potentialMatches.length > 0) {
        setResults(prev => prev + `💕 ${potentialMatches.length} potansiyel match bulundu!\n`);
        
        for (const matchUser of potentialMatches) {
          const ordered = getOrderedUserPair(likerUser.id, matchUser.id);
          const matchId = `${ordered.user1}_${ordered.user2}`;
          const { data: existingMatch } = await supabase
            .from('matches')
            .select('id')
            .eq('id', matchId)
            .single();

          if (!existingMatch) {
            const { error: matchError } = await supabase
              .from('matches')
              .insert({
                id: matchId,
                user1_id: ordered.user1,
                user2_id: ordered.user2,
                created_at: new Date().toISOString(),
                is_active: true
              });

            if (!matchError) {
              matchCount++;
            }
          }
        }
      }

      // 7. Özet
      setResults(prev => prev + `\n🏁 TÜM ERKEKLER BEĞENİLDİ!\n`);
      setResults(prev => prev + `📊 SONUÇ:\n`);
      setResults(prev => prev + `   ✅ Başarılı beğeni: ${successCount}\n`);
      setResults(prev => prev + `   ❌ Hatalı beğeni: ${errorCount}\n`);
      setResults(prev => prev + `   💕 Yeni match: ${matchCount}\n`);
      setResults(prev => prev + `   👤 Beğenen: ${likerUser.name}\n\n`);

      setFromEmail('');
    } catch (error: any) {
      setResults(prev => prev + `❌ [${timestamp}] Hata: ${error.message}\n\n`);
    } finally {
      setLoading(false);
    }
  };

  // 🚀 Herkesten beğeni gönder - sadece ERKEK kullanıcıların hedefi beğenmesi
  const handleMassLike = async () => {
    if (!massLikeEmail.trim()) {
      setResults(prev => prev + '❌ Lütfen hedef kullanıcının email adresini girin!\n');
      return;
    }

    setLoading(true);
    const timestamp = new Date().toLocaleTimeString();
    
    try {
      setResults(prev => prev + `🚀 [${timestamp}] Erkek kullanıcılardan beğeni gönderme başlatılıyor: ${massLikeEmail}\n`);

      // 1. Hedef kullanıcıyı bul
      const { data: targetUser, error: targetError } = await supabase
        .from('users')
        .select('id, name, email')
        .eq('email', massLikeEmail.trim())
        .single();

      if (targetError || !targetUser) {
        throw new Error(`Hedef kullanıcı bulunamadı: ${massLikeEmail}`);
      }

      setResults(prev => prev + `🎯 Hedef kullanıcı: ${targetUser.name} (${targetUser.email})\n`);

      // 2. Bu kullanıcıyı daha önce swipe etmiş olanları sayfalı çek (like veya pass)
      const existingSwipes = await fetchAllPages<{ swiper_id: string }>(
        (from, to) =>
          supabase
            .from('user_swipes')
            .select('swiper_id')
            .eq('swiped_id', targetUser.id)
            .range(from, to),
        'Mevcut swipe listesi'
      );

      const alreadySwipedUserIds = new Set(existingSwipes.map(l => l.swiper_id));
      setResults(prev => prev + `📊 Zaten swipe etmiş: ${alreadySwipedUserIds.size} kullanıcı\n`);

      // 3. Sadece ERKEK aktif kullanıcıları sayfalı çek (hedef kullanıcı hariç)
      const allMaleUsers = await fetchAllPages<{ id: string; name: string; email: string; gender: string }>(
        (from, to) =>
          supabase
            .from('users')
            .select('id, name, email, gender')
            .neq('id', targetUser.id)
            .eq('is_active', true)
            .in('gender', ['male', 'Erkek', 'erkek', 'Male'])
            .range(from, to),
        'Erkek kullanıcı listesi'
      );

      if (allMaleUsers.length === 0) {
        throw new Error('Beğeni gönderecek erkek kullanıcı bulunamadı');
      }

      // 4. Henüz swipe etmemiş erkek kullanıcıları filtrele
      const usersToLike = allMaleUsers.filter(u => !alreadySwipedUserIds.has(u.id));
      
      setResults(prev => prev + `👨 Toplam erkek kullanıcı: ${allMaleUsers.length}\n`);
      setResults(prev => prev + `💕 Beğeni gönderecek: ${usersToLike.length} erkek\n`);

      if (usersToLike.length === 0) {
        setResults(prev => prev + '✅ Tüm erkekler zaten bu kullanıcıyı swipe etmiş!\n\n');
        setMassLikeEmail('');
        return;
      }

      // 5. Her erkek kullanıcıdan beğeni oluştur (tek tek insert)
      let successCount = 0;
      let errorCount = 0;
      let matchCount = 0;

      for (let i = 0; i < usersToLike.length; i++) {
        const user = usersToLike[i];
        
        try {
          // user_swipes tablosuna upsert
          const { error: insertError } = await supabase
            .from('user_swipes')
            .upsert(
              {
                id: `${user.id}_${targetUser.id}`,
                swiper_id: user.id,
                swiped_id: targetUser.id,
                action: 'like',
                created_at: new Date().toISOString()
              },
              { onConflict: 'id' }
            );

          if (insertError) {
            console.error(`Kullanıcı ${user.id} hatası:`, insertError);
            errorCount++;
          } else {
            // user_likes tablosuna da ekle
            await supabase
              .from('user_likes')
              .upsert(
                {
                  from_user_id: user.id,
                  to_user_id: targetUser.id,
                  created_at: new Date().toISOString(),
                },
                { onConflict: 'from_user_id,to_user_id', ignoreDuplicates: true }
              );
            successCount++;
          }
        } catch (err) {
          errorCount++;
        }

        // Her 50 kullanıcıda bir ilerleme göster
        if ((i + 1) % 50 === 0 || i === usersToLike.length - 1) {
          const sc = successCount, ec = errorCount; // eslint fix
          setResults(prev => prev + `📍 İlerleme: ${i + 1}/${usersToLike.length} (%${Math.round(((i + 1) / usersToLike.length) * 100)}) - ✅${sc} ❌${ec}\n`);
        }

        // Rate limiting - her 10 istekte kısa bekleme
        if ((i + 1) % 10 === 0) {
          await new Promise(resolve => setTimeout(resolve, 50));
        }
      }

      // 6. Karşılıklı beğenileri kontrol et ve match oluştur
      setResults(prev => prev + `\n🔍 Karşılıklı beğeniler kontrol ediliyor...\n`);

      // Hedef kullanıcının kimleri beğendiğini bul
      const targetLikes = await fetchAllPages<{ swiped_id: string }>(
        (from, to) =>
          supabase
            .from('user_swipes')
            .select('swiped_id')
            .eq('swiper_id', targetUser.id)
            .eq('action', 'like')
            .range(from, to),
        'Hedef kullanıcının beğeni listesi'
      );

      if (targetLikes.length > 0) {
        const targetLikedIds = new Set(targetLikes.map(l => l.swiped_id));
        
        // Başarılı beğenen kullanıcılardan hangilerini hedef zaten beğenmiş?
        const successfulUsers = usersToLike.filter((_, idx) => idx < successCount);
        const potentialMatches = successfulUsers.filter(u => targetLikedIds.has(u.id));
        
        if (potentialMatches.length > 0) {
          setResults(prev => prev + `💕 ${potentialMatches.length} potansiyel match bulundu!\n`);
          
          // Match'leri oluştur
          for (const matchUser of potentialMatches) {
            const ordered = getOrderedUserPair(targetUser.id, matchUser.id);
            const matchId = `${ordered.user1}_${ordered.user2}`;
            
            // Match var mı kontrol et
            const { data: existingMatch } = await supabase
              .from('matches')
              .select('id')
              .eq('id', matchId)
              .single();

            if (!existingMatch) {
              const { error: matchError } = await supabase
                .from('matches')
                .insert({
                  id: matchId,
                  user1_id: ordered.user1,
                  user2_id: ordered.user2,
                  created_at: new Date().toISOString(),
                  is_active: true
                });

              if (!matchError) {
                matchCount++;
              }
            }
          }
        }
      }

      // 7. Özet
      setResults(prev => prev + `\n🏁 ERKEKLERDEN BEĞENİ GÖNDERİLDİ!\n`);
      setResults(prev => prev + `📊 SONUÇ:\n`);
      setResults(prev => prev + `   ✅ Başarılı beğeni: ${successCount}\n`);
      setResults(prev => prev + `   ❌ Hatalı beğeni: ${errorCount}\n`);
      setResults(prev => prev + `   💕 Yeni match: ${matchCount}\n`);
      setResults(prev => prev + `   🎯 Hedef: ${targetUser.name}\n\n`);

      // Form'u temizle
      setMassLikeEmail('');

    } catch (error: any) {
      setResults(prev => prev + `❌ [${timestamp}] Hata: ${error.message}\n\n`);
    } finally {
      setLoading(false);
    }
  };

  // 💕 Tüm erkekleri beğen - kullanıcı tüm erkekleri beğeniyor
  const handleLikeAllMales = async () => {
    if (!likeAllMalesEmail.trim()) {
      setResults(prev => prev + '❌ Lütfen kullanıcının email adresini girin!\n');
      return;
    }

    setLoading(true);
    const timestamp = new Date().toLocaleTimeString();
    
    try {
      setResults(prev => prev + `💕 [${timestamp}] Tüm erkekleri beğenme işlemi başlatılıyor: ${likeAllMalesEmail}\n`);

      // 1. Beğenecek kullanıcıyı bul
      const { data: likerUser, error: likerError } = await supabase
        .from('users')
        .select('id, name, email, gender')
        .eq('email', likeAllMalesEmail.trim())
        .single();

      if (likerError || !likerUser) {
        throw new Error(`Kullanıcı bulunamadı: ${likeAllMalesEmail}`);
      }

      setResults(prev => prev + `👤 Beğenen kullanıcı: ${likerUser.name} (${likerUser.email})\n`);

      // 2. Bu kullanıcının daha önce swipe ettiği kişileri sayfalı çek
      const existingSwipes = await fetchAllPages<{ swiped_id: string }>(
        (from, to) =>
          supabase
            .from('user_swipes')
            .select('swiped_id')
            .eq('swiper_id', likerUser.id)
            .range(from, to),
        'Mevcut swipe listesi'
      );

      const alreadySwipedUserIds = new Set(existingSwipes.map(l => l.swiped_id));
      setResults(prev => prev + `📊 Zaten swipe etmiş: ${alreadySwipedUserIds.size} kullanıcı\n`);

      // 3. Tüm ERKEK aktif kullanıcıları sayfalı çek (kendisi hariç)
      const allMaleUsers = await fetchAllPages<{ id: string; name: string; email: string; gender: string }>(
        (from, to) =>
          supabase
            .from('users')
            .select('id, name, email, gender')
            .neq('id', likerUser.id)
            .eq('is_active', true)
            .in('gender', ['male', 'Erkek', 'erkek', 'Male'])
            .range(from, to),
        'Erkek kullanıcı listesi'
      );

      if (allMaleUsers.length === 0) {
        throw new Error('Beğenilecek erkek kullanıcı bulunamadı');
      }

      // 4. Henüz swipe etmediği erkekleri filtrele
      const usersToLike = allMaleUsers.filter(u => !alreadySwipedUserIds.has(u.id));
      
      setResults(prev => prev + `👨 Toplam erkek kullanıcı: ${allMaleUsers.length}\n`);
      setResults(prev => prev + `💕 Beğenilecek erkek: ${usersToLike.length}\n`);

      if (usersToLike.length === 0) {
        setResults(prev => prev + '✅ Bu kullanıcı zaten tüm erkekleri beğenmiş!\n\n');
        setLikeAllMalesEmail('');
        return;
      }

      // 5. Her erkek için beğeni oluştur
      let successCount = 0;
      let errorCount = 0;
      let matchCount = 0;

      for (let i = 0; i < usersToLike.length; i++) {
        const maleUser = usersToLike[i];
        
        try {
          // user_swipes tablosuna upsert
          const { error: insertError } = await supabase
            .from('user_swipes')
            .upsert(
              {
                id: `${likerUser.id}_${maleUser.id}`,
                swiper_id: likerUser.id,
                swiped_id: maleUser.id,
                action: 'like',
                created_at: new Date().toISOString()
              },
              { onConflict: 'id' }
            );

          if (insertError) {
            console.error(`Erkek ${maleUser.id} beğenilemedi:`, insertError);
            errorCount++;
          } else {
            // user_likes tablosuna da ekle
            await supabase
              .from('user_likes')
              .upsert(
                {
                  from_user_id: likerUser.id,
                  to_user_id: maleUser.id,
                  created_at: new Date().toISOString(),
                },
                { onConflict: 'from_user_id,to_user_id', ignoreDuplicates: true }
              );
            successCount++;
          }
        } catch (err) {
          errorCount++;
        }

        // Her 50 kullanıcıda bir ilerleme göster
        if ((i + 1) % 50 === 0 || i === usersToLike.length - 1) {
          const sc = successCount, ec = errorCount;
          const progress = Math.round(((i + 1) / usersToLike.length) * 100);
          setResults(prev => prev + `📍 İlerleme: ${i + 1}/${usersToLike.length} (%${progress}) - ✅${sc} ❌${ec}\n`);
        }

        // Rate limiting
        if ((i + 1) % 10 === 0) {
          await new Promise(resolve => setTimeout(resolve, 50));
        }
      }

      // 6. Karşılıklı beğenileri kontrol et ve match oluştur
      setResults(prev => prev + `\n🔍 Karşılıklı beğeniler kontrol ediliyor...\n`);

      // Hangi erkekler bu kullanıcıyı beğenmiş?
      const whoLikesUser = await fetchAllPages<{ swiper_id: string }>(
        (from, to) =>
          supabase
            .from('user_swipes')
            .select('swiper_id')
            .eq('swiped_id', likerUser.id)
            .eq('action', 'like')
            .range(from, to),
        'Karşılıklı beğeni listesi'
      );

      const whoLikesIds = new Set(whoLikesUser.map(l => l.swiper_id));
      
      // Beğendiğimiz erkeklerden hangileri bizi de beğenmiş?
      const potentialMatches = usersToLike.filter(u => whoLikesIds.has(u.id));
      
      if (potentialMatches.length > 0) {
        setResults(prev => prev + `💕 ${potentialMatches.length} potansiyel match bulundu!\n`);
        
        // Match'leri oluştur
        for (const matchUser of potentialMatches) {
          const ordered = getOrderedUserPair(likerUser.id, matchUser.id);
          const matchId = `${ordered.user1}_${ordered.user2}`;
          
          // Match var mı kontrol et
          const { data: existingMatch } = await supabase
            .from('matches')
            .select('id')
            .eq('id', matchId)
            .single();

          if (!existingMatch) {
            const { error: matchError } = await supabase
              .from('matches')
              .insert({
                id: matchId,
                user1_id: ordered.user1,
                user2_id: ordered.user2,
                created_at: new Date().toISOString(),
                is_active: true
              });

            if (!matchError) {
              matchCount++;
            }
          }
        }
      }

      // 7. Özet
      setResults(prev => prev + `\n🏁 TÜM ERKEKLER BEĞENİLDİ!\n`);
      setResults(prev => prev + `📊 SONUÇ:\n`);
      setResults(prev => prev + `   ✅ Başarılı beğeni: ${successCount}\n`);
      setResults(prev => prev + `   ❌ Hatalı beğeni: ${errorCount}\n`);
      setResults(prev => prev + `   💕 Yeni match: ${matchCount}\n`);
      setResults(prev => prev + `   👤 Beğenen: ${likerUser.name}\n\n`);

      // Form'u temizle
      setLikeAllMalesEmail('');

    } catch (error: any) {
      setResults(prev => prev + `❌ [${timestamp}] Hata: ${error.message}\n\n`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '2rem'
      }}>
        <div>
          <h1 style={{ 
            color: '#2c3e50', 
            margin: '0 0 0.5rem 0',
            fontSize: '1.8rem',
            fontWeight: '600'
          }}>
            🛠️ Geliştirici Araçları
          </h1>
          <p style={{ color: '#7f8c8d', margin: 0 }}>
            Supabase tabanlı konum yönetim araçları
          </p>
        </div>
      </div>

      {/* Konum Yönetim Araçları */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '10px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        padding: '2rem',
        marginBottom: '2rem',
        border: '2px solid #e3f2fd'
      }}>
        <h3 style={{ 
          margin: '0 0 1rem 0', 
          color: '#2c3e50',
          fontSize: '1.3rem',
          borderBottom: '2px solid #FF9800',
          paddingBottom: '0.5rem'
        }}>
          📍 Konum Yönetimi
        </h3>

        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: '1rem'
        }}>
          <button
            onClick={handleAssignIstanbulLocations}
            disabled={loading}
            style={{
              backgroundColor: loading ? '#ccc' : '#FF9800',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '1.5rem',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1,
              textAlign: 'left',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
              }
            }}
          >
            <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1.1rem' }}>
              {loading ? '⏳ İşleniyor...' : '📍 İstanbul Civarına Konum Ata'}
            </h4>
            <p style={{ margin: 0, fontSize: '0.9rem', opacity: 0.9 }}>
              Tüm kullanıcılara İstanbul çevresinde rastgele konum atar (Supabase tabanlı)
            </p>
          </button>

            <button
            onClick={() => setShowManualLikeForm(!showManualLikeForm)}
              disabled={loading}
              style={{
              backgroundColor: loading ? '#ccc' : '#F78D93',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '1.5rem',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1,
                textAlign: 'left',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                }
              }}
            >
              <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1.1rem' }}>
              💖 Manual Like Ekle
              </h4>
              <p style={{ margin: 0, fontSize: '0.9rem', opacity: 0.9 }}>
              İstediğiniz kullanıcılar arasında like ilişkisi oluşturun (Supabase tabanlı)
              </p>
            </button>

            <button
            onClick={handleCreate50RandomPosts}
              disabled={loading}
              style={{
              backgroundColor: loading ? '#ccc' : '#9C27B0',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '1.5rem',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1,
                textAlign: 'left',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                }
              }}
            >
              <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1.1rem' }}>
              {loading ? '⏳ Postlar Oluşturuluyor...' : '📝 50 Fake Kadın Post Oluştur'}
              </h4>
              <p style={{ margin: 0, fontSize: '0.9rem', opacity: 0.9 }}>
              25 fotoğraflı + 25 sadece metin post oluştur (fake kadın kullanıcılar)
              </p>
            </button>

            <button
            onClick={() => setShowMassLikeForm(!showMassLikeForm)}
              disabled={loading}
              style={{
              backgroundColor: loading ? '#ccc' : '#E91E63',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '1.5rem',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1,
                textAlign: 'left',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                }
              }}
            >
              <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1.1rem' }}>
              🚀 Erkeklerden Beğeni Gönder
              </h4>
              <p style={{ margin: 0, fontSize: '0.9rem', opacity: 0.9 }}>
              Bir kullanıcıyı daha önce beğenmemiş tüm erkeklerin beğenmesini sağla
              </p>
            </button>

            <button
            onClick={() => setShowLikeAllMalesForm(!showLikeAllMalesForm)}
              disabled={loading}
              style={{
              backgroundColor: loading ? '#ccc' : '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '1.5rem',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1,
                textAlign: 'left',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                }
              }}
            >
              <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1.1rem' }}>
              💕 Tüm Erkekleri Beğen
              </h4>
              <p style={{ margin: 0, fontSize: '0.9rem', opacity: 0.9 }}>
              Bir kullanıcının tüm erkekleri beğenmesini sağla
              </p>
            </button>
        </div>

        <div style={{
          backgroundColor: '#f8f9fa',
          padding: '1.5rem',
          borderRadius: '8px',
          border: '1px solid #e9ecef',
          marginTop: '1.5rem'
        }}>
          <h4 style={{ margin: '0 0 0.5rem 0', color: '#495057' }}>📋 İşlem Detayları:</h4>
          <ul style={{ margin: 0, paddingLeft: '1.5rem', color: '#6c757d' }}>
            <li>🎯 Hedef: Supabase users tablosundaki tüm kullanıcılar</li>
            <li>📍 Konum: İstanbul koordinatları (40.8-41.2, 28.6-29.2)</li>
            <li>🔧 Güncelleme: location field'ı JSON formatında</li>
            <li>⚡ Performans: 10'luk gruplar halinde batch işleme</li>
            <li>🛡️ Güvenlik: Sadece konum bilgisi güncellenir</li>
            <li>💖 Manual Like: Yazılan kullanıcı tüm erkekleri beğenir</li>
            <li>💕 Match Sistemi: Karşılıklı like durumunda otomatik eşleşme</li>
            <li>📝 Post Oluşturma: 50 farklı gerçekçi içerik + görsel</li>
            <li>🖼️ Görsel Karışımı: %60 görselli, %40 sadece yazı</li>
            <li>🎯 Post Hedefi: Supabase posts tablosuna eklenir</li>
            <li>🚀 Erkeklerden Beğeni: Hedef kullanıcıyı beğenmemiş tüm erkekler beğenir</li>
            <li>⚡ Tek Tek İşlem: Hata önlemek için tek tek insert yapılır</li>
          </ul>
        </div>
      </div>

      {/* 🆕 Manual Like Form */}
      {showManualLikeForm && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '10px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          padding: '2rem',
          marginBottom: '2rem',
          border: '2px solid #F78D93'
        }}>
          <h3 style={{ 
            margin: '0 0 2rem 0', 
            color: '#2c3e50',
            fontSize: '1.3rem',
            borderBottom: '2px solid #F78D93',
            paddingBottom: '0.5rem'
          }}>
            💖 Manual Like (Tüm Erkekleri Beğen)
          </h3>

          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: '1rem',
            marginBottom: '1rem'
          }}>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem',
                fontWeight: 'bold',
                color: '#2c3e50'
              }}>
                👤 Beğenecek Kullanıcı Email (bu kullanıcı tüm erkekleri beğenecek):
              </label>
              <input
                type="email"
                value={fromEmail}
                onChange={(e) => setFromEmail(e.target.value)}
                placeholder="kullanici@gmail.com"
                style={{
                  width: '100%',
                  maxWidth: '400px',
                  padding: '0.75rem',
                  border: '2px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                }}
                onFocus={(e) => e.target.style.borderColor = '#F78D93'}
                onBlur={(e) => e.target.style.borderColor = '#ddd'}
                disabled={loading}
              />
            </div>

          </div>

          <div style={{
            display: 'flex',
            gap: '1rem',
            alignItems: 'center'
          }}>
            <button
              onClick={handleManualLike}
              disabled={loading || !fromEmail.trim()}
              style={{
                backgroundColor: fromEmail.trim() && !loading ? '#F78D93' : '#ccc',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '0.75rem 2rem',
                fontSize: '1rem',
                cursor: fromEmail.trim() && !loading ? 'pointer' : 'not-allowed',
                transition: 'all 0.2s ease'
              }}
            >
              {loading ? '⏳ İşleniyor...' : '💕 Tüm Erkekleri Beğen'}
            </button>

            <button
              onClick={() => {
                setFromEmail('');
              }}
              disabled={loading}
              style={{
                backgroundColor: 'transparent',
                color: '#666',
                border: '2px solid #ddd',
                borderRadius: '8px',
                padding: '0.75rem 1.5rem',
                fontSize: '1rem',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              🗑️ Temizle
            </button>

            <button
              onClick={() => setShowManualLikeForm(false)}
              disabled={loading}
              style={{
                backgroundColor: 'transparent',
                color: '#F78D93',
                border: '2px solid #F78D93',
                borderRadius: '8px',
                padding: '0.75rem 1.5rem',
                fontSize: '1rem',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              ❌ Kapat
            </button>
          </div>

          <div style={{
            marginTop: '1rem',
            padding: '1rem',
            backgroundColor: '#f8f9fa',
            borderRadius: '6px',
            fontSize: '0.9rem',
            color: '#666'
          }}>
            <strong>💡 Bilgi:</strong> Bu işlem geri alınamaz.
            <br />
            • Girdiğiniz email'e sahip kullanıcı, daha önce beğenmediği <strong>TÜM ERKEK kullanıcıları</strong> beğenecek
            <br />
            • Karşılıklı beğeni varsa otomatik match oluşturulacak
            <br />
            • Supabase 1000 satır limiti için sayfalı çekim kullanılır
          </div>
        </div>
      )}

      {/* 🚀 Erkeklerden Beğeni Gönder Form */}
      {showMassLikeForm && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '10px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          padding: '2rem',
          marginBottom: '2rem',
          border: '2px solid #E91E63'
        }}>
          <h3 style={{ 
            margin: '0 0 2rem 0', 
            color: '#2c3e50',
            fontSize: '1.3rem',
            borderBottom: '2px solid #E91E63',
            paddingBottom: '0.5rem'
          }}>
            🚀 Erkeklerden Beğeni Gönder
          </h3>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '0.5rem',
              fontWeight: 'bold',
              color: '#2c3e50'
            }}>
              🎯 Hedef Kullanıcı Email (bu kullanıcı beğenilecek):
            </label>
            <input
              type="email"
              value={massLikeEmail}
              onChange={(e) => setMassLikeEmail(e.target.value)}
              placeholder="hedef@gmail.com"
              style={{
                width: '100%',
                maxWidth: '400px',
                padding: '0.75rem',
                border: '2px solid #ddd',
                borderRadius: '8px',
                fontSize: '1rem',
                outline: 'none',
                transition: 'border-color 0.2s',
              }}
              onFocus={(e) => e.target.style.borderColor = '#E91E63'}
              onBlur={(e) => e.target.style.borderColor = '#ddd'}
              disabled={loading}
            />
          </div>

          <div style={{
            display: 'flex',
            gap: '1rem',
            alignItems: 'center'
          }}>
            <button
              onClick={handleMassLike}
              disabled={loading || !massLikeEmail.trim()}
              style={{
                backgroundColor: massLikeEmail.trim() && !loading ? '#E91E63' : '#ccc',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '0.75rem 2rem',
                fontSize: '1rem',
                cursor: massLikeEmail.trim() && !loading ? 'pointer' : 'not-allowed',
                transition: 'all 0.2s ease'
              }}
            >
              {loading ? '⏳ İşleniyor...' : '🚀 Erkeklerden Beğeni Gönder'}
            </button>

            <button
              onClick={() => setMassLikeEmail('')}
              disabled={loading}
              style={{
                backgroundColor: 'transparent',
                color: '#666',
                border: '2px solid #ddd',
                borderRadius: '8px',
                padding: '0.75rem 1.5rem',
                fontSize: '1rem',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              🗑️ Temizle
            </button>

            <button
              onClick={() => setShowMassLikeForm(false)}
              disabled={loading}
              style={{
                backgroundColor: 'transparent',
                color: '#E91E63',
                border: '2px solid #E91E63',
                borderRadius: '8px',
                padding: '0.75rem 1.5rem',
                fontSize: '1rem',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              ❌ Kapat
            </button>
          </div>

          <div style={{
            marginTop: '1rem',
            padding: '1rem',
            backgroundColor: '#fce4ec',
            borderRadius: '6px',
            fontSize: '0.9rem',
            color: '#880e4f'
          }}>
            <strong>⚠️ Dikkat:</strong> Bu işlem geri alınamaz!
            <br />
            • Girdiğiniz email'e sahip kullanıcıyı, daha önce beğenmemiş <strong>TÜM ERKEK kullanıcılar</strong> beğenecek
            <br />
            • Karşılıklı beğeni varsa otomatik match oluşturulacak
            <br />
            • İşlem erkek kullanıcı sayısına bağlı olarak birkaç dakika sürebilir
          </div>
        </div>
      )}

      {/* 💕 Tüm Erkekleri Beğen Form */}
      {showLikeAllMalesForm && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '10px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          padding: '2rem',
          marginBottom: '2rem',
          border: '2px solid #4CAF50'
        }}>
          <h3 style={{ 
            margin: '0 0 2rem 0', 
            color: '#2c3e50',
            fontSize: '1.3rem',
            borderBottom: '2px solid #4CAF50',
            paddingBottom: '0.5rem'
          }}>
            💕 Tüm Erkekleri Beğen
          </h3>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '0.5rem',
              fontWeight: 'bold',
              color: '#2c3e50'
            }}>
              👤 Beğenecek Kullanıcı Email (bu kullanıcı tüm erkekleri beğenecek):
            </label>
            <input
              type="email"
              value={likeAllMalesEmail}
              onChange={(e) => setLikeAllMalesEmail(e.target.value)}
              placeholder="kullanici@gmail.com"
              style={{
                width: '100%',
                maxWidth: '400px',
                padding: '0.75rem',
                border: '2px solid #ddd',
                borderRadius: '8px',
                fontSize: '1rem',
                outline: 'none',
                transition: 'border-color 0.2s',
              }}
              onFocus={(e) => e.target.style.borderColor = '#4CAF50'}
              onBlur={(e) => e.target.style.borderColor = '#ddd'}
              disabled={loading}
            />
          </div>

          <div style={{
            display: 'flex',
            gap: '1rem',
            alignItems: 'center'
          }}>
            <button
              onClick={handleLikeAllMales}
              disabled={loading || !likeAllMalesEmail.trim()}
              style={{
                backgroundColor: likeAllMalesEmail.trim() && !loading ? '#4CAF50' : '#ccc',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '0.75rem 2rem',
                fontSize: '1rem',
                cursor: likeAllMalesEmail.trim() && !loading ? 'pointer' : 'not-allowed',
                transition: 'all 0.2s ease'
              }}
            >
              {loading ? '⏳ İşleniyor...' : '💕 Tüm Erkekleri Beğen'}
            </button>

            <button
              onClick={() => setLikeAllMalesEmail('')}
              disabled={loading}
              style={{
                backgroundColor: 'transparent',
                color: '#666',
                border: '2px solid #ddd',
                borderRadius: '8px',
                padding: '0.75rem 1.5rem',
                fontSize: '1rem',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              🗑️ Temizle
            </button>

            <button
              onClick={() => setShowLikeAllMalesForm(false)}
              disabled={loading}
              style={{
                backgroundColor: 'transparent',
                color: '#4CAF50',
                border: '2px solid #4CAF50',
                borderRadius: '8px',
                padding: '0.75rem 1.5rem',
                fontSize: '1rem',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              ❌ Kapat
            </button>
          </div>

          <div style={{
            marginTop: '1rem',
            padding: '1rem',
            backgroundColor: '#e8f5e9',
            borderRadius: '6px',
            fontSize: '0.9rem',
            color: '#1b5e20'
          }}>
            <strong>💡 Bilgi:</strong> Bu işlem geri alınamaz!
            <br />
            • Girdiğiniz email'e sahip kullanıcı, daha önce beğenmediği <strong>TÜM ERKEK kullanıcıları</strong> beğenecek
            <br />
            • Karşılıklı beğeni varsa otomatik match oluşturulacak
            <br />
            • İşlem erkek kullanıcı sayısına bağlı olarak birkaç dakika sürebilir
          </div>
        </div>
      )}

      {/* Sonuç Konsolu */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '10px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        padding: '2rem'
      }}>
        <h3 style={{ 
          margin: '0 0 1rem 0', 
          color: '#2c3e50',
          fontSize: '1.3rem'
        }}>
          📊 İşlem Sonuçları
        </h3>
        
        <div style={{
          backgroundColor: '#1e1e1e',
          color: '#00ff00',
          padding: '1rem',
          borderRadius: '8px',
          fontFamily: 'Courier New, monospace',
          fontSize: '0.9rem',
          minHeight: '200px',
          maxHeight: '400px',
          overflow: 'auto',
          whiteSpace: 'pre-wrap'
        }}>
          {results || '📍 Supabase konum yönetim araçları hazır...\n\n💡 İstanbul civarına konum atama:\n  • Tüm kullanıcılara rastgele İstanbul koordinatları atar\n  • Batch işleme ile güvenli güncelleme\n  • Real-time ilerleme takibi\n\n💖 Manual Like Ekle:\n  • Email adreslerine göre kullanıcılar arası beğeni oluştur\n  • Otomatik karşılıklı like kontrolü\n  • Match sistemi entegrasyonu'}
        </div>
        
        {results && (
          <button
            onClick={() => setResults('')}
            style={{
              marginTop: '1rem',
              padding: '0.5rem 1rem',
              backgroundColor: '#666',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            🗑️ Konsolu Temizle
          </button>
        )}
      </div>
    </div>
  );
};

export default DeveloperToolsPage; 